import { uniqueId } from 'lodash'
import { ReactNode, useMemo, useRef, useState } from 'react'
import { Range } from 'types'
import Fill from './fill'
import { Handle, HANDLE_WIDTH, Slider } from './style'

type Props = {
    children?: ReactNode
    className?: string
    disabled?: boolean
    dndId?: string
    handleColor: string
    handlesOnly?: boolean
    maxWidth: number
    range: Range
    onChange?: (range: Range) => void
}

const RangeSliderDnd = ({
    children,
    className,
    disabled,
    dndId,
    handleColor,
    handlesOnly = false,
    maxWidth: maxWidthProp,
    range,
    onChange,
}: Props) => {
    const idRef = useRef<string>(dndId || uniqueId('range-input-slider'))
    const [startOffset, setStartOffset] = useState<number>(0)
    const [endOffset, setEndOffset] = useState<number>(0)

    const maxWidth = maxWidthProp - HANDLE_WIDTH
    const startInPixels = useMemo(() => Math.round(range.start * maxWidth), [maxWidth, range.start])
    const endInPixels = useMemo(() => Math.round(range.end * maxWidth), [maxWidth, range.end])

    const controlValue: Range = useMemo(
        () => ({
            end: (endInPixels + endOffset) / maxWidth,
            start: (startInPixels + startOffset) / maxWidth,
        }),
        [endOffset, endInPixels, maxWidth, startOffset, startInPixels]
    )

    const onFillDrag = (x: number) => {
        setStartOffset(x)
        setEndOffset(x)
    }

    const updateRange = () => {
        onChange?.(controlValue)
        setEndOffset(0)
        setStartOffset(0)
    }

    return (
        <Slider
            className={className}
            style={{
                left: startInPixels + startOffset,
                right: maxWidth - (endInPixels + endOffset),
            }}
        >
            <Handle
                controlColor={handleColor}
                controlId={idRef.current}
                controlType="range-start"
                controlValue={controlValue}
                disabled={disabled}
                max={endInPixels - startInPixels}
                min={-startInPixels}
                onDrag={setStartOffset}
                onDragEnd={updateRange}
            />
            <Fill
                controlId={idRef.current}
                controlType="range"
                controlValue={controlValue}
                disabled={handlesOnly || disabled}
                max={maxWidth - endInPixels}
                min={-startInPixels}
                onDrag={onFillDrag}
                onDragEnd={updateRange}
            >
                {children}
            </Fill>
            <Handle
                controlColor={handleColor}
                controlId={idRef.current}
                controlType="range-end"
                controlValue={controlValue}
                disabled={disabled}
                max={maxWidth - endInPixels}
                min={startInPixels - endInPixels}
                onDrag={setEndOffset}
                onDragEnd={updateRange}
            />
        </Slider>
    )
}

export default RangeSliderDnd
